import html2canvas from "html2canvas"
import { MAX_OVERFLOW_PX } from "~/utils/shareCardLayout"

export interface ShareImageOptions {
  quality?: number
  scale?: number
}

export interface ShareImageResult {
  dataUrl: string
  blob: Blob
}

const CONTENT_SELECTOR = '[data-share-card-content="true"]'

function waitForNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

function waitForImageLoad(img: HTMLImageElement): Promise<void> {
  if (img.complete) {
    if (img.naturalWidth === 0) {
      return Promise.reject(new Error(`Failed to load image: ${img.src || "unknown"}`))
    }
    return img.decode?.().catch(() => undefined) ?? Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      img.removeEventListener("load", onLoad)
      img.removeEventListener("error", onError)
    }

    const onLoad = () => {
      cleanup()
      void (img.decode?.().catch(() => undefined) ?? Promise.resolve()).then(() => resolve())
    }

    const onError = () => {
      cleanup()
      reject(new Error(`Failed to load image: ${img.src || "unknown"}`))
    }

    img.addEventListener("load", onLoad)
    img.addEventListener("error", onError)
  })
}

export async function waitForImagesInElement(element: HTMLElement): Promise<void> {
  const images = Array.from(element.querySelectorAll("img"))
  if (images.length === 0) return

  await Promise.all(images.map((img) => waitForImageLoad(img)))
}

export async function waitForCaptureReady(element: HTMLElement): Promise<void> {
  await waitForImagesInElement(element)
  if (document.fonts?.ready) {
    await document.fonts.ready
  }
  await waitForNextFrame()
}

export interface ShareCardFitResult {
  applied: boolean
  scale: number
}

export function fitShareCardContentToCanvas(element: HTMLElement): ShareCardFitResult {
  const content = element.querySelector(CONTENT_SELECTOR) as HTMLElement | null
  if (!content) return { applied: false, scale: 1 }

  const canvasHeight = element.clientHeight
  const contentHeight = content.scrollHeight
  const overflow = contentHeight - canvasHeight

  if (overflow <= MAX_OVERFLOW_PX) {
    return { applied: false, scale: 1 }
  }

  const scale = canvasHeight / contentHeight
  content.style.transform = `scale(${scale})`
  content.style.transformOrigin = "top center"
  content.style.width = "100%"

  return { applied: true, scale }
}

export function resetShareCardContentFit(element: HTMLElement): void {
  const content = element.querySelector(CONTENT_SELECTOR) as HTMLElement | null
  if (!content) return

  content.style.transform = ""
  content.style.transformOrigin = ""
  content.style.width = ""
}

export function shareGenerationErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    if (/failed to load image/i.test(err.message)) {
      console.error("Share card image load failed:", err)
      return "Could not load images for the share card. Check your connection and try again."
    }
    return err.message
  }

  return "Failed to generate image"
}

export async function generateShareImage(
  element: HTMLElement,
  options: ShareImageOptions = {},
): Promise<ShareImageResult> {
  const { quality = 0.95, scale = 2 } = options

  await waitForCaptureReady(element)
  const fit = fitShareCardContentToCanvas(element)

  try {
    // useCORS: cross-origin images need ACAO from the asset host (e.g. S3 CORS for https://app.dynamo.dog).
    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      logging: false,
      backgroundColor: null,
      width: element.offsetWidth,
      height: element.offsetHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      onclone: (clonedDocument) => {
        // html2canvas currently fails on modern oklab/oklch CSS functions.
        // The share card is fully inline-styled, so removing stylesheet nodes
        // avoids unsupported color parsing in global Tailwind output.
        clonedDocument.querySelectorAll('style,link[rel="stylesheet"]').forEach((node) => {
          node.remove()
        })

        if (fit.applied) {
          const clonedRoot = clonedDocument.querySelector('[data-shareable-image="true"]') as HTMLElement | null
          const clonedContent = clonedRoot?.querySelector(CONTENT_SELECTOR) as HTMLElement | null
          if (clonedContent) {
            clonedContent.style.transform = `scale(${fit.scale})`
            clonedContent.style.transformOrigin = "top center"
            clonedContent.style.width = "100%"
          }
        }
      },
    })

    const dataUrl = canvas.toDataURL("image/png", quality)

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (nextBlob) => {
          if (!nextBlob) {
            reject(new Error("Failed to generate image blob"))
            return
          }
          resolve(nextBlob)
        },
        "image/png",
        quality,
      )
    })

    return { dataUrl, blob }
  } finally {
    if (fit.applied) {
      resetShareCardContentFit(element)
    }
  }
}

export function downloadShareImage(dataUrl: string, filename = "doggy-dynamo-share-card.png"): void {
  const link = document.createElement("a")
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
